import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { jobReportEndpoints } from '@/lib/api/endpoints';
import { reportKeys } from '@/lib/queryKeys';
import { apiGet, apiPost, apiPatch, apiDelete, operationsClient } from '@/lib/api/client';
import type { ReportSectionWire } from '@/lib/api/types/reports';

export interface ReportSummary {
  id: string;
  number: string;
  title: string;
  status: string;
  templateId: string;
  ownerType: string;
  ownerId: string;
  customerId: string;
  publishedAt: string | null;
  signedAt: string | null;
  archivedAt: string | null;
  spawnedEstimateId: string | null;
  shareToken: string | null;
  photoCount: number;
  sectionCount: number;
  createdAt: string;
  updatedAt: string;
}

export function useReports(filters: Record<string, unknown> = {}, options?: { enabled?: boolean }) {
  return useApiQuery<{ data: ReportSummary[]; nextCursor: string | null }>({
    queryKey: reportKeys.list(filters),
    queryFn: async () => apiGet(operationsClient, jobReportEndpoints.list(), { params: filters }),
    enabled: options?.enabled ?? true,
    showErrorToast: false,
  });
}

export interface JobReportsResponse {
  data: ReportSummary[];
  primaryReportId: string | null;
}

export function useJobReports(jobId: string | undefined, options?: { enabled?: boolean }) {
  return useApiQuery<JobReportsResponse>({
    queryKey: jobId ? reportKeys.forJob(jobId) : ['reports', 'for-job', 'none'],
    queryFn: async () => apiGet(operationsClient, jobReportEndpoints.forJob(jobId as string)),
    enabled: Boolean(jobId) && (options?.enabled ?? true),
    showErrorToast: false,
  });
}

export function useReport(id: string | undefined) {
  return useApiQuery({
    queryKey: id ? reportKeys.detail(id) : ['reports', 'detail', 'none'],
    queryFn: async () => apiGet(operationsClient, jobReportEndpoints.detail(id as string)),
    enabled: Boolean(id),
    showErrorToast: false,
  });
}

export function useCreateReport() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (input: {
      templateId: string;
      ownerType: 'JOB' | 'LEAD' | 'CUSTOMER';
      ownerId: string;
      title?: string;
      assetIds?: string[];
    }) => apiPost(operationsClient, jobReportEndpoints.create(), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function usePublishReport(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (changeSummary?: string) =>
      apiPost(operationsClient, jobReportEndpoints.publish(id), {
        changeSummary,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

/**
 * Spec §3.4 — open a working revision on a published report. The report goes
 * editable again while customers keep seeing the published version, so the
 * detail cache has to be refetched before the editor unlocks.
 */
export function useBeginReportRevision(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async () => apiPost(operationsClient, jobReportEndpoints.revise(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

/** Abandon the working revision — the report rows revert to the published snapshot. */
export function useDiscardReportRevision(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async () => apiPost(operationsClient, jobReportEndpoints.discardRevision(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

/** SIGNED → PUBLISHED. The captured signature is retained as audit. */
export function useUnsignReport(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async () => apiPost(operationsClient, jobReportEndpoints.unsign(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export interface ReportRevisionSummary {
  id: string;
  revisionNumber: number;
  authorUserId: string;
  changeSummary: string | null;
  publishedAt: string | null;
  createdAt: string;
}

/** Version history — newest first. */
export function useReportRevisions(id: string, options?: { enabled?: boolean }) {
  return useApiQuery<ReportRevisionSummary[]>({
    queryKey: reportKeys.revisions(id),
    queryFn: async () => apiGet(operationsClient, jobReportEndpoints.revisions(id)),
    enabled: options?.enabled ?? true,
    showErrorToast: false,
  });
}

export interface ReportFieldWire {
  key: string;
  label: string;
  group: string;
  value: string | null;
}

/**
 * Merge fields with the values this report resolves to — powers the `{{`
 * autocomplete and the chip labels in the editor.
 */
export function useReportFields(id: string, options?: { enabled?: boolean }) {
  return useApiQuery<ReportFieldWire[]>({
    queryKey: reportKeys.fields(id),
    queryFn: async () => apiGet(operationsClient, jobReportEndpoints.fields(id)),
    enabled: options?.enabled ?? true,
    showErrorToast: false,
  });
}

/** The same catalog without values, for editing a template. */
export function useReportFieldCatalog(options?: { enabled?: boolean }) {
  return useApiQuery<ReportFieldWire[]>({
    queryKey: reportKeys.fieldCatalog(),
    queryFn: async () => apiGet(operationsClient, jobReportEndpoints.fieldCatalog()),
    enabled: options?.enabled ?? true,
    staleTime: Infinity,
    showErrorToast: false,
  });
}

export function useSubmitReportForReview(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (changeSummary?: string) =>
      apiPost(operationsClient, jobReportEndpoints.submitForReview(id), {
        changeSummary,
      }),
    // Appends a revision, so the version history has to refetch as well —
    // reportKeys.all() is a prefix of revisions()/list(), which covers both.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useApproveReport(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async () => apiPost(operationsClient, jobReportEndpoints.approve(id), {}),
    // Approve publishes, so this mints a revision the version labels read.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export function useSendBackReport(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (reason: string) =>
      apiPost(operationsClient, jobReportEndpoints.sendBack(id), { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
    },
  });
}

/**
 * Spec Appendix A — set or clear the "primary" report for a Job. Surfaces
 * one report as the headline on the Job (the diagnostic that drove the
 * follow-on work, etc.). Pass `null` to clear.
 */
export function useSetJobPrimaryReport(jobId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (reportId: string | null) =>
      apiPatch(operationsClient, jobReportEndpoints.setJobPrimary(jobId), { reportId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.forJob(jobId) });
    },
  });
}

export function useArchiveReport(id: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async () => apiPost(operationsClient, jobReportEndpoints.archive(id), {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(id) });
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

export type ReportShareAccessMode = 'PUBLIC_LINK' | 'EMAIL_GATED' | 'PORTAL_ONLY';

export interface ReportShareInfo {
  share: {
    id: string;
    token: string;
    accessMode: ReportShareAccessMode;
    expiresAt: string | null;
    revokedAt: string | null;
  } | null;
  accessMode: ReportShareAccessMode | null;
  expiresAt: string | null;
  lastAccessedAt: string | null;
  recentAccessCount: number;
}

/**
 * Spec §7.2 — fetch the active share + access summary (mode, expiry,
 * last view, recent view count).
 */
export function useReportShareInfo(reportId: string, options?: { enabled?: boolean }) {
  return useApiQuery<ReportShareInfo>({
    queryKey: reportKeys.shareInfo(reportId),
    queryFn: async () => apiGet(operationsClient, jobReportEndpoints.shareInfo(reportId)),
    enabled: options?.enabled ?? true,
    showErrorToast: false,
  });
}

/**
 * Spec §7.2 — create or rotate the share token. Pass `accessMode` to
 * switch between PUBLIC_LINK / EMAIL_GATED / PORTAL_ONLY. Rotating
 * also rotates the token, invalidating any leaked link.
 */
export function useRotateReportShare(reportId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (input: { accessMode?: ReportShareAccessMode; expiryDays?: number }) =>
      apiPost(operationsClient, jobReportEndpoints.share(reportId), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
      qc.invalidateQueries({ queryKey: reportKeys.shareInfo(reportId) });
    },
  });
}

interface UploadPhotoArgs {
  file: File;
  sectionId?: string;
  caption?: string;
}

/** Wire shape of a confirmed ReportPhoto (subset the editor needs). */
export interface ReportPhotoDto {
  id: string;
  storageUrl: string;
  thumbnailUrl: string;
  caption: string | null;
  annotationLayer?: unknown;
}

/**
 * Spec §5.2 — three-step photo upload: request a presigned PUT URL,
 * upload the bytes directly to S3, then confirm the upload so the
 * backend records metadata + links the photo to its report/section.
 * Dimensions are read locally from the image before upload so the
 * confirm DTO doesn't require a server-side probe.
 */
export function useUploadReportPhoto(reportId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async ({ file, sectionId, caption }: UploadPhotoArgs) => {
      const { width, height } = await readImageDimensions(file);
      const presign = (await apiPost(operationsClient, jobReportEndpoints.uploadUrl(reportId), {
        filename: file.name,
        mime: file.type,
        sizeBytes: file.size,
      })) as {
        storageKey: string;
        thumbnailKey: string;
        uploadUrl: string;
      };
      const putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!putRes.ok) {
        throw new Error(`Photo upload failed: ${putRes.status} ${putRes.statusText}`);
      }
      return apiPost<ReportPhotoDto>(operationsClient, jobReportEndpoints.photoConfirm(reportId), {
        storageKey: presign.storageKey,
        thumbnailKey: presign.thumbnailKey,
        originalFilename: file.name,
        mime: file.type,
        sizeBytes: file.size,
        width,
        height,
        ...(sectionId ? { sectionId } : {}),
        ...(caption ? { caption } : {}),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
      qc.invalidateQueries({ queryKey: reportKeys.photos(reportId) });
    },
  });
}

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image dimensions'));
    };
    img.src = url;
  });
}

export interface SuggestedRecommendation {
  pricebookItemId: string | null;
  name: string | null;
  code: string | null;
  qty: number;
  reason: string;
  suggestedPrice?: number;
}

/**
 * Spec §10.4 — AI extracts pricebook recommendations from the report's
 * Findings text. The backend calls OpenAI with the tenant pricebook as
 * grounding so suggestions are real SKUs. Pure-suggestion: the user
 * still has to confirm before lines are added.
 */
export function useSuggestRecommendations(reportId: string) {
  return useApiMutation({
    mutationFn: async () =>
      apiPost<{ items: SuggestedRecommendation[] }>(
        operationsClient,
        jobReportEndpoints.aiSuggestRecommendations(reportId),
        {},
      ),
  });
}

/**
 * Spec §10.1 — Whisper transcribes an audio blob captured from the
 * browser mic. Axios stringifies plain objects but leaves FormData
 * alone, so passing a FormData here gets the right multipart
 * encoding + boundary header.
 */
export function useTranscribeAudio(reportId: string) {
  return useApiMutation({
    mutationFn: async (audio: Blob) => {
      const form = new FormData();
      const filename = audio.type.includes('webm')
        ? 'recording.webm'
        : audio.type.includes('mp4')
          ? 'recording.mp4'
          : 'recording.wav';
      form.append('file', audio, filename);
      return apiPost<{ text: string }>(
        operationsClient,
        jobReportEndpoints.aiTranscribe(reportId),
        form,
      );
    },
  });
}

/**
 * Persist a caption / annotation-layer edit on a ReportPhoto. Invalidates
 * the parent report so image nodes re-render with the new metadata.
 */
export function useUpdateReportPhoto(reportId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async ({
      photoId,
      ...input
    }: {
      photoId: string;
      caption?: string;
      annotationLayer?: Record<string, unknown>;
    }) => apiPatch(operationsClient, jobReportEndpoints.photoDetail(reportId, photoId), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
      qc.invalidateQueries({ queryKey: reportKeys.photos(reportId) });
    },
  });
}

/**
 * Remove a report photo and every document reference to it. The backend owns
 * the reference sweep and photoCount decrement; invalidating every report list
 * makes the Job's Reports tab reflect that count immediately.
 */
export function useDeleteReportPhoto(reportId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (photoId: string) =>
      apiDelete(operationsClient, jobReportEndpoints.photoDetail(reportId, photoId)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
      qc.invalidateQueries({ queryKey: reportKeys.photos(reportId) });
      qc.invalidateQueries({ queryKey: reportKeys.all() });
    },
  });
}

/**
 * Spec §10.3 — AI drafts a report summary from the section contents.
 * The backend serializes the doc JSON to text before prompting.
 */
export function useSuggestReportSummary(reportId: string) {
  return useApiMutation({
    mutationFn: async () =>
      apiPost<{ summary: string }>(
        operationsClient,
        jobReportEndpoints.aiSuggestSummary(reportId),
        {},
      ),
  });
}

// ─── Sections (doc-editor redo plan §2.1) ──────────────────────────────────

/** Shape of the cached report detail the section mutations touch. */
interface ReportDetailCacheShape {
  sections?: ReportSectionWire[];
}

export function useCreateReportSection(reportId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (input: { title: string; order?: number }) =>
      apiPost<ReportSectionWire>(operationsClient, jobReportEndpoints.sections(reportId), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
}

export type UpdateReportSectionInput = {
  sectionId: string;
  title?: string;
  subtitle?: string | null;
  content?: unknown;
  customerVisible?: boolean;
  pageBreakAfter?: boolean;
  hiddenWhenEmpty?: boolean;
};

/**
 * Section autosave + metadata edits — `PATCH /reports/:id/sections/:sid`.
 *
 * Content saves happen on a debounce while the user is still typing, so this
 * deliberately patches the detail cache instead of invalidating: a refetch
 * racing the next keystroke would clobber the live editor state.
 */
export function useUpdateReportSection(reportId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async ({ sectionId, ...patch }: UpdateReportSectionInput) =>
      apiPatch(operationsClient, jobReportEndpoints.section(reportId, sectionId), patch),
    onSuccess: (_data, { sectionId, ...patch }) => {
      qc.setQueryData(reportKeys.detail(reportId), (prev: unknown) => {
        const detail = prev as ReportDetailCacheShape | undefined;
        if (!detail?.sections) return prev;
        return {
          ...detail,
          sections: detail.sections.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)),
        };
      });
    },
  });
}

export function useDeleteReportSection(reportId: string) {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: async (sectionId: string) =>
      apiDelete(operationsClient, jobReportEndpoints.section(reportId, sectionId)),
    onSuccess: (_data, sectionId) => {
      qc.setQueryData(reportKeys.detail(reportId), (prev: unknown) => {
        const detail = prev as ReportDetailCacheShape | undefined;
        if (!detail?.sections) return prev;
        return { ...detail, sections: detail.sections.filter((s) => s.id !== sectionId) };
      });
      qc.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
}

/**
 * `POST /reports/:id/sections/reorder { sectionIds }` — reorders optimistically
 * so the sidebar doesn't flicker, then reconciles with the server.
 */
export function useReorderReportSections(reportId: string) {
  const qc = useQueryClient();
  return useApiMutation<unknown, string[], { previous: unknown }>({
    mutationFn: async (sectionIds: string[]) =>
      apiPost(operationsClient, jobReportEndpoints.sectionsReorder(reportId), { sectionIds }),
    onMutate: async (sectionIds) => {
      await qc.cancelQueries({ queryKey: reportKeys.detail(reportId) });
      const previous = qc.getQueryData(reportKeys.detail(reportId));
      qc.setQueryData(reportKeys.detail(reportId), (prev: unknown) => {
        const detail = prev as ReportDetailCacheShape | undefined;
        if (!detail?.sections) return prev;
        const byId = new Map(detail.sections.map((s) => [s.id, s]));
        const reordered = sectionIds
          .map((id, index) => {
            const section = byId.get(id);
            return section ? { ...section, order: index } : null;
          })
          .filter((s): s is ReportSectionWire => s !== null);
        return { ...detail, sections: reordered };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context) qc.setQueryData(reportKeys.detail(reportId), context.previous);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: reportKeys.detail(reportId) });
    },
  });
}
